import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  postRequestAuthenticated,
  postRequestUnAuthenticated,
  postRequestAuthenticatedWithFile,
} from "./../../services/rest";
import { updateChannel } from "./channelItemsSlice";
import { createGeneralTopic } from "./channelItemsSlice";
import { removeMember } from "./reorderTopicSlice";

export const removeCover = createAsyncThunk(
  "channel/remove-cover",
  async (channelId, { rejectWithValue }) => {
    try {
      const response = await postRequestAuthenticated("/remove/channel/cover", {
        channelId,
      });
      if (response.success) {
        return response.channel;
      } else {
        return rejectWithValue(response.message);
      }
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);
export const saveCover = createAsyncThunk(
  "channel/save-cover",
  async (channel, { rejectWithValue }) => {
    try {
      const response = await postRequestAuthenticatedWithFile(
        "/save/channel/cover",
        channel
      );
      if (response.success) {
        return response.channel;
      } else {
        return rejectWithValue(response.message);
      }
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);
export const fetchChannel = createAsyncThunk(
  "channel/fetch-channel",
  async (channelId, { rejectWithValue }) => {
    try {
      // Pass the logged-in user's id so the backend can resolve isOwner/
      // isMember/requestStatus. Without it every channel came back isMember:false
      // (so a joined channel kept showing "Join channel" after switching).
      let user_id;
      try {
        user_id = JSON.parse(localStorage.getItem("user") || "{}")?._id;
      } catch (e) {}
      const response = await postRequestUnAuthenticated("/fetch/channel", {
        id: channelId,
        user_id,
      });
      if (response.success) {
        return response.channel;
      } else {
        return rejectWithValue(response.message);
      }
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const createChannelInvite = createAsyncThunk(
  "channel/create-channel-invite",
  async (id, { rejectWithValue }) => {
    try {
      const response = await postRequestAuthenticated(
        "/create/channel/invite",
        {
          channelId: id,
        }
      );
      if (response.success) {
        return response.invite;
      } else {
        return rejectWithValue(response.message);
      }
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const joinChannel = createAsyncThunk(
  "channel/join-channel",
  async (id, { rejectWithValue }) => {
    try {
      const response = await postRequestAuthenticated("/join/channel", {
        channelId: id,
      });
      if (response.success) {
        return response;
      } else {
        return rejectWithValue(response.message);
      }
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);
export const leaveChannel = createAsyncThunk(
  "channel/leave-channel",
  async (id, { rejectWithValue }) => {
    try {
      const response = await postRequestAuthenticated("/leave/channel", {
        channelId: id,
      });
      if (response.success) {
        return response.channel;
      } else {
        return rejectWithValue(response.message);
      }
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);
export const joinChannelInvite = createAsyncThunk(
  "channel/join-channel-invite",
  async (data, { rejectWithValue }) => {
    try {
      const response = await postRequestAuthenticated(
        "/join/channel/invite",
        data
      );
      if (response.success) {
        return response.channel;
      } else {
        return rejectWithValue(response.message);
      }
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

const initialState = {
  _id: "",
  name: "",
  visibility: "anyone",
  editability: "me",
  members: [],
  cover_image: null,
  admins: [],
  paywall: false,
  requests: [],
  topics: [],
  logo: null,
  description: "",
  channelstatus: "idle",
  loading: false,
  channelNameError: false,
  isEdit: false,
  code: "",
};

export const channelSlice = createSlice({
  name: "channel",
  initialState,
  reducers: {
    // setSelectedUnsplashImage: (state, action) => {
    //   state.cover_image = action.payload;
    //   state.imageSource = "unsplash";
    // },
    setChannelItems: (state, action) => {
      return { ...state, ...action.payload };
    },
    setChannelField: (state, action) => {
      const { field, value } = action.payload;
      state[field] = value;
    },

    clearChannel: (state) => {
      state.name = "";
      state._id = "";
      state.visibility = "anyone";
      state.cover_image = null;
      state.logo = null;
      state.cover_image = null;
      state.visibility = [];
      state.admins = [];
      state.editability = "me";
      state.requests = [];
      state.members = [];
      state.name = "";
      state.description = "";
      state.channelstatus = "idle";
      state.channelNameError = false;
      state.isEdit = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(removeCover.pending, (state) => {
        state.channelstatus = "loading";
      })
      .addCase(removeCover.fulfilled, (state, action) => {
        state.channelstatus = "idle";
        state.cover_image = "";
      })
      .addCase(removeCover.rejected, (state, action) => {
        state.channelstatus = "idle";
        state.channelNameError = action.payload || action.error.message;
      })
      .addCase(saveCover.pending, (state) => {
        state.channelstatus = "loading";
      })
      .addCase(saveCover.fulfilled, (state, action) => {
        state.channelstatus = "idle";
        state.cover_image = action.payload.cover_image;
      })
      .addCase(saveCover.rejected, (state, action) => {
        state.channelstatus = "idle";
        state.channelNameError = action.payload || action.error.message;
      })
      .addCase(createGeneralTopic.fulfilled, (state, action) => {
        state.channelstatus = "idle";
        const topic = action.payload;
        state.topics.unshift(topic._id);
      })
      .addCase(fetchChannel.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchChannel.fulfilled, (state, action) => {
        state.loading = false;
        Object.assign(state, initialState, action.payload);
      })
      .addCase(fetchChannel.rejected, (state, action) => {
        state.loading = false;
        state.channelNameError = action.payload || action.error.message;
      })
      .addCase(joinChannel.pending, (state) => {
        state.channelstatus = "loading";
      })
      .addCase(joinChannel.fulfilled, (state, action) => {
        state.channelstatus = "idle";
        const r = action.payload || {};
        // Update the relationship flags the UI now reads (joined immediately,
        // or "request" for invite-only) instead of pushing into a members array
        // whose element shape no longer matches.
        if (!r.channel?._id || state._id === r.channel._id) {
          if (r.joined) {
            state.isMember = true;
            state.requestStatus = "joined";
            if (typeof state.memberCount === "number") state.memberCount += 1;
          } else if (r.success) {
            state.requestStatus = "request";
          }
        }
      })
      .addCase(joinChannel.rejected, (state, action) => {
        state.channelstatus = "idle";
      })
      .addCase(leaveChannel.pending, (state) => {
        state.channelstatus = "loading";
      })
      .addCase(leaveChannel.fulfilled, (state, action) => {
        state.channelstatus = "idle";
        const channel = action.payload;
        if (channel && state._id === channel._id) {
          state.isMember = false;
          state.requestStatus = null;
          if (typeof state.memberCount === "number" && state.memberCount > 0) {
            state.memberCount -= 1;
          }
        }
      })
      .addCase(leaveChannel.rejected, (state, action) => {
        state.channelstatus = "idle";
      })
      .addCase(removeMember.fulfilled, (state, action) => {
        state.status = "idle";
        const removeData = action.payload;
        if (state._id === removeData.channelId) {
          let memberIndex = state.members.findIndex(
            (member) => member === removeData.userId
          );
          if (memberIndex !== -1) {
            state.members.splice(memberIndex, 1);
          }
        }
      })
      .addCase(updateChannel.fulfilled, (state, action) => {
        if (action.payload._id === state._id) {
          Object.assign(state, initialState, action.payload);
        }
      });
  },
});

export const {
  setSelectedUnsplashImage,
  setChannelField,
  clearChannel,
  setChannelItems,
} = channelSlice.actions;

export default channelSlice.reducer;
